import React from 'react';
import clsx from 'clsx';
import styles from './Textarea.module.css';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: React.Ref<HTMLTextAreaElement>;
  label?: string | React.ReactNode;
  error?: string | React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  wrapperClassName?: string;
  dataTestId?: string;
}

function Textarea({
  ref,
  label,
  error,
  leftIcon,
  rightIcon,
  required,
  className,
  wrapperClassName,
  id,
  dataTestId,
  ...props
}: TextareaProps) {
  // Generate ID for label association if not provided
  const generatedId = React.useId();
  const textareaId = id || `textarea-${generatedId}`;

  return (
    <div className={clsx(styles.textareaWrapper, wrapperClassName)}>
      {label && (
        <label htmlFor={textareaId} className={styles.label} data-test-id={dataTestId ? `${dataTestId}-label` : undefined}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.textareaContainer}>
        {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}

        <textarea
          id={textareaId}
          className={clsx(
            styles.textarea,
            leftIcon && styles.textareaWithLeftIcon,
            rightIcon && styles.textareaWithRightIcon,
            error && styles.textareaError,
            className
          )}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${textareaId}-error` : undefined}
          data-test-id={dataTestId ? `${dataTestId}-textarea` : undefined}
          {...props}
          ref={ref}
        />

        {rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
      </div>

      {error && (
        <div
          id={`${textareaId}-error`}
          className={styles.errorMessage}
          role="alert"
          data-test-id={dataTestId ? `${dataTestId}-error` : 'validation-error'}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default Textarea;
